import { useState, useEffect } from 'react';

export type Countdown = {
  days: number;
  hours: number;
  minutes: number;
} | null;

const getCountdown = (targetDate: string, now: number): Countdown => {
  const targetTime = new Date(targetDate).getTime();

  if (Number.isNaN(targetTime)) return null;

  const diff = targetTime - now;

  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes };
};

/**
 * Hook that returns a live countdown to a target date.
 * Updates every minute and returns null when the target date has passed.
 *
 * @param targetDate - ISO 8601 date string (e.g., "2026-02-14T08:00:00-08:00")
 * @returns Countdown object with days, hours, minutes, or null if date has passed
 */
export const useCountdown = (targetDate: string | undefined): Countdown => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!targetDate) {
      return;
    }

    const interval = setInterval(() => {
      setNow(Date.now());
    }, 60_000);

    return () => clearInterval(interval);
  }, [targetDate]);

  if (!targetDate) return null;

  return getCountdown(targetDate, now);
};
